<template>
  <div class="checkout-page">
    <div class="container mx-auto px-3 md:px-4 py-4 md:py-8">
      <!-- Loading Spinner -->
      <div v-if="loading" class="py-16 max-w-[1408px] mx-auto">
        <LoadingSpinner />
      </div>

      <!-- Error Message -->
      <div v-else-if="error" class="py-16 text-center max-w-[1408px] mx-auto">
        <p class="text-red-500 text-lg mb-4">{{ error }}</p>
        <button @click="loadCheckoutData" class="bg-empire-yellow px-6 py-2 font-archivo-narrow font-semibold">
          {{ $t('common.retry') }}
        </button>
      </div>

      <div v-else class="max-w-[1408px] mx-auto">
        <!-- Title -->
        <div class="pb-6 text-center">
          <h1 class="font-archivo-narrow font-semibold text-2xl md:text-[34px] leading-[30px] md:leading-[40px]">{{ $t('checkout.title') }}</h1>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <!-- Personal Details Form -->
          <div class="lg:col-span-8">
            <div class="space-y-4 md:space-y-8 bg-[#FAFAFA] p-4 md:p-8">
              <!-- Personal Details Section (temporariamente oculto) -->
              <section v-if="false">
                <div class="flex items-center cursor-pointer lg:cursor-default mb-6 relative pr-12" @click="toggleSection('personal')">
                  <h2 class="font-archivo-narrow font-semibold text-xl md:text-2xl">{{ $t('checkout.personalDetails') }}</h2>
                  <svg
                    class="section-arrow lg:hidden absolute right-0"
                    :class="{ 'rotate-270': sections.personal }"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M9.4 18L8 16.6L12.6 12L8 7.4L9.4 6L15.4 12L9.4 18Z" fill="black"/>
                  </svg>
                </div>
                <div v-show="sections.personal || isDesktop" class="grid grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <label class="block font-archivo text-sm mb-2">{{ $t('checkout.firstName') }}</label>
                    <input
                      type="text"
                      v-model="formData.firstName"
                      :placeholder="$t('checkout.firstNamePlaceholder')"
                      :class="[
                        'w-full p-2 md:p-4 border-2 rounded font-archivo text-sm md:text-base',
                        (showErrors && formErrors.firstName) ? 'border-red-500' : 'border-black/25',
                        isFieldDisabled.firstName ? 'bg-gray-100' : 'bg-white'
                      ]"
                      :disabled="isFieldDisabled.firstName"
                    >
                    <span v-if="showErrors && formErrors.firstName" class="text-red-500 text-sm mt-1">
                      {{ $t('checkout.fieldRequired') }}
                    </span>
                  </div>
                  <div>
                    <label class="block font-archivo text-sm mb-2">{{ $t('checkout.lastName') }}</label>
                    <input
                      type="text"
                      v-model="formData.lastName"
                      :placeholder="$t('checkout.lastNamePlaceholder')"
                      :class="[
                        'w-full p-2 md:p-4 border-2 rounded font-archivo text-sm md:text-base',
                        (showErrors && formErrors.lastName) ? 'border-red-500' : 'border-black/25',
                        isFieldDisabled.lastName ? 'bg-gray-100' : 'bg-white'
                      ]"
                      :disabled="isFieldDisabled.lastName"
                    >
                    <span v-if="showErrors && formErrors.lastName" class="text-red-500 text-sm mt-1">
                      {{ $t('checkout.fieldRequired') }}
                    </span>
                  </div>
                  <div>
                    <label class="block font-archivo text-sm mb-2">{{ $t('checkout.email') }}</label>
                    <input
                      type="email"
                      v-model="formData.email"
                      :placeholder="$t('checkout.emailPlaceholder')"
                      :class="[
                        'w-full p-2 md:p-4 border-2 rounded font-archivo text-sm md:text-base',
                        (showErrors && formErrors.email) ? 'border-red-500' : 'border-black/25',
                        isFieldDisabled.email ? 'bg-gray-100' : 'bg-white'
                      ]"
                      :disabled="isFieldDisabled.email"
                    >
                    <span v-if="showErrors && formErrors.email" class="text-red-500 text-sm mt-1">
                      {{ $t('checkout.fieldRequired') }}
                    </span>
                  </div>
                  <div>
                    <label class="block font-archivo text-sm mb-2">{{ $t('checkout.phone') }}</label>
                    <input
                      type="tel"
                      v-model="formData.phone"
                      :placeholder="$t('checkout.phonePlaceholder')"
                      :class="[
                        'w-full p-2 md:p-4 border-2 rounded font-archivo text-sm md:text-base bg-white',
                        (showErrors && formErrors.phone) ? 'border-red-500' : 'border-black/25'
                      ]"
                    >
                    <span v-if="showErrors && formErrors.phone" class="text-red-500 text-sm mt-1">
                      {{ $t('checkout.fieldRequired') }}
                    </span>
                  </div>
                </div>
              </section>

              <!-- P.O. Number and Location Section -->
              <section>
                <div class="flex items-center cursor-pointer lg:cursor-default mb-6 relative pr-12">
                  <h2 class="font-archivo-narrow font-semibold text-xl md:text-2xl">{{ $t('checkout.poNumber') }}</h2>
                </div>
                <div class="mb-6">
                  <!-- P.O. Number -->
                  <div>
                    <input
                      type="text"
                      v-model="formData.poNumber"
                      :placeholder="$t('checkout.poNumberPlaceholder')"
                      class="w-full p-2 md:p-4 border-2 border-black/25 rounded font-archivo text-sm md:text-base"
                    >
                  </div>
                </div>



                <!-- Delivery Method: allowPickup only if client_id = master_client_id in the project (API) -->
                <div class="mb-6">
                  <div class="flex gap-3 md:gap-4">
                    <button
                      type="button"
                      @click="setDeliveryMethod('delivery')"
                      :class="[
                        'flex-1 px-4 py-2 md:px-6 md:py-3 font-archivo-narrow font-semibold text-base md:text-lg rounded',
                        deliveryMethod === 'delivery' ? 'bg-empire-yellow text-black' : 'bg-gray-300 text-black'
                      ]"
                    >
                      {{ $t('checkout.delivery') }}
                    </button>
                    <!-- PickUp only if allowPickup (API: client_id = master_client_id in the effective project) -->
                    <button
                      v-if="allowPickup"
                      type="button"
                      @click="setDeliveryMethod('pickup')"
                      :class="[
                        'flex-1 px-4 py-2 md:px-6 md:py-3 font-archivo-narrow font-semibold text-base md:text-lg rounded transition-all cursor-pointer hover:bg-gray-400',
                        deliveryMethod === 'pickup' ? 'bg-empire-yellow text-black' : 'bg-gray-300 text-black'
                      ]"
                    >
                      {{ $t('checkout.pickup') }}
                    </button>
                  </div>
                </div>
              </section>


              <!-- Shipping Address Section -->
              <section>
                <div class="flex items-center justify-between cursor-pointer lg:cursor-default mb-6 relative pr-12" @click="toggleSection('shipping')">
                  <h2 class="font-archivo-narrow font-semibold text-xl md:text-2xl">{{ $t('checkout.shippingDetails') }}</h2>

                  <div class="flex items-center lg:absolute lg:right-0 lg:top-0">
                    <button
                      @click.stop="openAddressModal"
                      class="bg-empire-yellow text-black px-3 py-1.5 rounded text-sm font-archivo font-medium mr-4 hover:bg-empire-yellow/90 transition-colors"
                    >
                      {{ deliveryMethod === 'delivery' ? $t('checkout.changeAddress') : $t('checkout.changeLocation') }}
                    </button>

                    <!-- Arrow for mobile -->
                    <svg
                      class="section-arrow lg:hidden ml-4 absolute right-0"
                      :class="{ 'rotate-270': sections.shipping }"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M9.4 18L8 16.6L12.6 12L8 7.4L9.4 6L15.4 12L9.4 18Z" fill="black"/>
                    </svg>
                  </div>
                </div>
                <div v-show="sections.shipping || isDesktop">
                  <!-- Delivery Mode -->
                  <div v-if="deliveryMethod === 'delivery'" class="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                    <!-- Row 1: Address Line 1 -->
                    <div class="col-span-1 md:col-span-3">
                      <label class="block font-archivo text-sm mb-2">{{ $t('checkout.addressLine1') }}</label>
                      <input
                        type="text"
                        v-model="formData.address"
                        @input="handleAddressFieldEdit"
                        :placeholder="$t('checkout.addressLine1Placeholder')"
                        required
                        :class="[
                          'w-full p-2 md:p-4 border-2 rounded font-archivo text-sm md:text-base bg-white',
                          (showErrors && formErrors.address) ? 'border-red-500' : 'border-black/25'
                        ]"
                      >
                      <span v-if="showErrors && formErrors.address" class="text-red-500 text-sm mt-1">
                        {{ $t('checkout.fieldRequired') }}
                      </span>
                    </div>

                    <!-- Row 2: Address Line 2 (formerly Landmark) -->
                    <div class="col-span-1 md:col-span-3">
                      <label class="block font-archivo text-sm mb-2">{{ $t('checkout.addressLine2') }}</label>
                      <input
                        type="text"
                        v-model="formData.landmark"
                        @input="handleAddressFieldEdit"
                        :placeholder="$t('checkout.addressLine2Placeholder')"
                        :class="[
                          'w-full p-2 md:p-4 border-2 rounded font-archivo text-sm md:text-base bg-white',
                          (showErrors && formErrors.landmark) ? 'border-red-500' : 'border-black/25'
                        ]"
                      >
                      <span v-if="showErrors && formErrors.landmark" class="text-red-500 text-sm mt-1">
                        {{ $t('checkout.fieldRequired') }}
                      </span>
                    </div>

                    <!-- Row 3: Country and State -->
                    <div class="col-span-1 md:col-span-2">
                      <label class="block font-archivo text-sm mb-2">{{ $t('checkout.country') }}</label>
                      <CustomSelect
                        v-model="selectedCountryId"
                        :options="countryOptions"
                        :placeholder="$t('checkout.selectCountry')"
                        :error="showErrors && formErrors.country"
                        :error-message="$t('checkout.fieldRequired')"
                        required
                        @change="onCountryChange"
                      />
                    </div>

                    <div class="col-span-1 md:col-span-1">
                      <label class="block font-archivo text-sm mb-2">{{ $t('checkout.state') }}</label>
                      <CustomSelect
                        v-model="selectedProvinceId"
                        :options="provinceOptions"
                        :placeholder="$t('checkout.selectState')"
                        :disabled="!selectedCountryId"
                        :error="showErrors && formErrors.state"
                        :error-message="$t('checkout.fieldRequired')"
                        required
                        @change="onProvinceChange"
                      />
                    </div>

                    <!-- Row 4: City and Postal Code -->
                    <div class="col-span-1 md:col-span-2">
                      <label class="block font-archivo text-sm mb-2">{{ $t('checkout.city') }}</label>
                      <input
                        type="text"
                        v-model="formData.city"
                        @input="handleAddressFieldEdit"
                        :placeholder="$t('checkout.cityPlaceholder')"
                        required
                        :class="[
                          'w-full p-2 md:p-4 border-2 rounded font-archivo text-sm md:text-base bg-white',
                          (showErrors && formErrors.city) ? 'border-red-500' : 'border-black/25'
                        ]"
                      >
                      <span v-if="showErrors && formErrors.city" class="text-red-500 text-sm mt-1">
                        {{ $t('checkout.fieldRequired') }}
                      </span>
                    </div>

                    <div class="col-span-1 md:col-span-1">
                      <label class="block font-archivo text-sm mb-2">{{ $t('checkout.postalCode') }}</label>
                      <input
                        type="text"
                        v-model="formData.postalCode"
                        @input="handleAddressFieldEdit"
                        :placeholder="$t('checkout.postalCodePlaceholder')"
                        required
                        :class="[
                          'w-full p-2 md:p-4 border-2 rounded font-archivo text-sm md:text-base bg-white',
                          (showErrors && formErrors.postalCode) ? 'border-red-500' : 'border-black/25'
                        ]"
                      >
                      <span v-if="showErrors && formErrors.postalCode" class="text-red-500 text-sm mt-1">
                        {{ $t('checkout.fieldRequired') }}
                      </span>
                    </div>

                    <!-- Row 4: Contact on Site, Contact Phone, Delivery Date/Time -->
                    <div class="col-span-1 md:col-span-1">
                      <label class="block font-archivo text-sm mb-2">{{ $t('checkout.contactOnSite') }}</label>
                      <input
                        type="text"
                        v-model="formData.contactOnSite"
                        :placeholder="$t('checkout.contactOnSitePlaceholder')"
                        class="w-full p-2 md:p-4 border-2 border-black/25 rounded font-archivo text-sm md:text-base bg-white"
                      >
                    </div>
                    <div class="col-span-1 md:col-span-1">
                      <label class="block font-archivo text-sm mb-2">{{ $t('checkout.contactPhone') }}</label>
                      <input
                        type="tel"
                        v-model="formData.contactPhone"
                        :placeholder="$t('checkout.contactPhonePlaceholder')"
                        class="w-full p-2 md:p-4 border-2 border-black/25 rounded font-archivo text-sm md:text-base bg-white"
                      >
                    </div>
                    <div class="col-span-1 md:col-span-1">
                      <label class="block font-archivo text-sm mb-2">{{ $t('checkout.deliveryDate') }}</label>
                      <PickCalendar
                        v-model="formData.deliveryDate"
                        :min-date="minDateISO"
                        :placeholder="'YYYY/MM/DD'"
                        input-class="w-full p-2 md:p-4 border-2 border-black/25 rounded font-archivo text-sm md:text-base bg-white"
                      />
                    </div>

                    <!-- Row 5: Special Delivery Instructions -->
                    <div class="col-span-1 md:col-span-3">
                      <label class="block font-archivo text-sm mb-2">{{ $t('checkout.specialInstructions') }}</label>
                      <textarea
                        v-model="formData.specialInstructions"
                        :placeholder="$t('checkout.specialInstructionsPlaceholder')"
                        rows="3"
                        class="w-full p-2 md:p-4 border-2 border-black/25 rounded font-archivo text-sm md:text-base bg-white"
                      ></textarea>
                    </div>

                    <!-- Hidden fields for backend compatibility -->
                    <input type="hidden" v-model="formData.number">
                    <input type="hidden" v-model="formData.apartment">
                  </div>

                  <!-- Pick-up Mode -->
                  <div v-else class="space-y-4">
                    <!-- Location Information and Pickup Date - Desktop Layout -->
                    <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <!-- Location Information - 3/4 columns on desktop -->
                      <div class="md:col-span-3">
                        <div class="bg-gray-100 p-4 rounded-lg h-full">
                          <div>
                            <h3 class="font-archivo-narrow font-semibold text-lg mb-2">{{ $t('checkout.pickupLocation') }}</h3>
                            <p class="font-archivo text-base">
                              {{ selectedLocation ? selectedLocation.description : $t('checkout.selectLocation') }}
                            </p>
                          </div>
                        </div>
                      </div>

                      <!-- Pickup Date - 1/4 column on desktop -->
                      <div class="md:col-span-1">
                        <label class="block font-archivo text-sm mb-2">{{ $t('checkout.pickupDate') }}</label>
                        <PickCalendar
                          v-model="formData.deliveryDate"
                          :min-date="minDateISO"
                          :placeholder="'YYYY/MM/DD'"
                          input-class="w-full p-2 md:p-4 border-2 border-black/25 rounded font-archivo text-sm md:text-base bg-white h-[calc(100%-2rem)]"
                        />
                      </div>
                    </div>

                    <!-- Modo retirada: rótulo/placeholder específicos (i18n) -->
                    <div>
                      <label class="block font-archivo text-sm mb-2">{{ $t('checkout.specialInstructionsPickup') }}</label>
                      <textarea
                        v-model="formData.specialInstructions"
                        :placeholder="$t('checkout.specialInstructionsPlaceholderPickup')"
                        rows="3"
                        class="w-full p-2 md:p-4 border-2 border-black/25 rounded font-archivo text-sm md:text-base bg-white"
                      ></textarea>
                    </div>
                  </div>
                </div>
              </section>

              <!-- Payment Section - Oculto permanentemente -->
              <section v-if="false">
                <div class="flex items-center cursor-pointer lg:cursor-default mb-6 relative pr-12" @click="toggleSection('payment')">
                  <h2 class="font-archivo-narrow font-semibold text-xl md:text-2xl">{{ $t('checkout.paymentDetails') }}</h2>

                  <!-- Card brands -->
                  <div class="flex flex-wrap items-center gap-2 sm:gap-4 lg:ml-8">
                    <img src="/images/payment/visa.png" alt="Visa" class="h-6 sm:h-8">
                    <img src="/images/payment/master.png" alt="Mastercard" class="h-6 sm:h-8">
                    <img src="/images/payment/paypal.png" alt="PayPal" class="h-6 sm:h-8">
                    <img src="/images/payment/stripe.png" alt="Stripe" class="h-6 sm:h-8">
                    <img src="/images/payment/google.png" alt="Google Pay" class="h-6 sm:h-8">
                  </div>

                  <!-- Arrow for mobile -->
                  <svg
                    class="section-arrow lg:hidden absolute right-0"
                    :class="{ 'rotate-270': sections.payment }"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M9.4 18L8 16.6L12.6 12L8 7.4L9.4 6L15.4 12L9.4 18Z" fill="black"/>
                  </svg>
                </div>
                <div v-show="sections.payment || isDesktop" class="grid grid-cols-2 gap-3 md:gap-4">
                  <div class="col-span-2">
                    <label class="block font-archivo text-sm mb-2">{{ $t('checkout.cardHolder') }}</label>
                    <input
                      type="text"
                      v-model="formData.cardHolder"
                      :placeholder="$t('checkout.cardHolderPlaceholder')"
                      class="w-full p-2 md:p-4 border-2 border-black/25 rounded font-archivo text-sm md:text-base bg-gray-100"
                      disabled
                    >
                  </div>
                  <div class="col-span-2">
                    <label class="block font-archivo text-sm mb-2">{{ $t('checkout.cardNumber') }}</label>
                    <input
                      type="text"
                      v-model="formData.cardNumber"
                      :placeholder="$t('checkout.cardNumberPlaceholder')"
                      class="w-full p-2 md:p-4 border-2 border-black/25 rounded font-archivo text-sm md:text-base bg-gray-100"
                      disabled
                    >
                  </div>
                  <div>
                    <label class="block font-archivo text-sm mb-2">{{ $t('checkout.expiryDate') }}</label>
                    <input
                      type="text"
                      v-model="formData.expiryDate"
                      :placeholder="$t('checkout.expiryDatePlaceholder')"
                      class="w-full p-2 md:p-4 border-2 border-black/25 rounded font-archivo text-sm md:text-base bg-gray-100"
                      disabled
                    >
                  </div>
                  <div>
                    <label class="block font-archivo text-sm mb-2">{{ $t('checkout.cvv') }}</label>
                    <input
                      type="text"
                      v-model="formData.cvv"
                      :placeholder="$t('checkout.cvvPlaceholder')"
                      class="w-full p-2 md:p-4 border-2 border-black/25 rounded font-archivo text-sm md:text-base bg-gray-100"
                      disabled
                    >
                  </div>
                </div>
              </section>
            </div>
          </div>

          <!-- Order Summary -->
          <div class="lg:col-span-4">
            <div class="w-full lg:w-[528px] bg-white">
              <div class="flex flex-col gap-3 md:gap-4 p-3 md:p-4">
                <!-- Header -->
                <div class="flex justify-center items-center w-full h-[50px] md:h-[72px] bg-[#FAFAFA]">
                  <h2 class="font-archivo-narrow font-semibold text-2xl md:text-[34px] leading-[30px] md:leading-[40px] text-center mx-auto">{{ $t('checkout.summary.title') }}</h2>
                </div>

                <!-- Loading Spinner for the order summary -->
                <div v-if="isLoadingSummary" class="py-8 flex justify-center">
                  <LoadingSpinner :text="$t('checkout.processing')" />
                </div>

                <template v-else-if="cartItems.length > 0">
                  <!-- Products List -->
                  <div class="space-y-0">
                    <div v-for="(item, index) in cartItems" :key="index"
                      class="flex items-start py-4 md:py-8 border-b border-black/25">
                      <!-- Product Image -->
                      <router-link :to="`/product/${item.id}`">
                        <img
                          :src="getProductImage(item.image, item)"
                          :alt="item.name"
                          class="w-[80px] h-[80px] md:w-[120px] md:h-[110px] object-contain"
                          @error="handleImageError"
                        />
                      </router-link>

                      <!-- Container for description, name and price -->
                      <div class="flex-1 min-w-0 mx-4"> <!-- min-w-0 allows the text to be truncated -->
                        <div class="flex flex-col gap-2">
                          <!-- Product description in bold (swapped with the name) -->
                          <div class="h-[2.4em] overflow-hidden">
                            <router-link :to="`/product/${item.id}`" class="hover:text-empire-yellow transition-colors">
                              <p class="font-archivo font-bold text-sm md:text-base leading-[1.2em] text-black/90 description-fixed-height">
                                {{ getProductDescription(item) }}
                              </p>
                            </router-link>
                          </div>

                          <!-- Product Name with Quantity -->
                          <div class="flex items-center gap-2 min-w-0"> <!-- min-w-0 allows the text to be truncated -->
                            <div class="flex-shrink-0 flex justify-center items-center min-w-[32px] h-[22px] px-1 bg-black">
                              <span class="font-archivo font-semibold text-xs text-empire-yellow">{{ item.quantity }}x</span>
                            </div>
                            <router-link :to="`/product/${item.id}`" class="font-archivo-narrow font-light text-[12px] md:text-[14px] leading-[14px] md:leading-[16px] text-black/70 truncate hover:text-empire-yellow transition-colors uppercase product-name">{{ item.name }}</router-link>
                          </div>

                          <!-- Product characteristics -->
                          <div v-if="item.color || item.size || item.weight || item.unit" class="flex flex-col gap-0.5 md:gap-1 mt-0.5 md:mt-1">
                            <span v-if="item.color" class="font-archivo text-sm text-black/70 flex items-center gap-2">
                              <span class="font-semibold">{{ $t('productDetails.selectColor') }}:</span>
                              <span class="flex items-center gap-2">
                                <span
                                  class="inline-block rounded-full border-2 transition-all duration-200"
                                  :style="{
                                    backgroundColor: item.color,
                                    borderColor: isWhiteOrLight(item.color) ? '#CCCCCC' : 'transparent',
                                    width: '14px',
                                    height: '14px'
                                  }"
                                  :class="[
                                    isWhiteOrLight(item.color) ? 'ring-1 ring-gray-200' : ''
                                  ]"
                                ></span>
                              </span>
                            </span>
                            <span v-if="item.size" class="font-archivo text-sm text-black/70">
                              <span class="font-semibold">{{ $t('productDetails.selectSize') }}:</span> {{ item.size }}
                            </span>
                            <span v-if="item.weight" class="font-archivo text-sm text-black/70">
                              <span class="font-semibold">{{ $t('productDetails.selectWeight') }}:</span> {{ item.weight }}
                            </span>
                            <span v-if="item.unit" class="font-archivo text-sm text-black/70">
                              <span class="font-semibold">{{ $t('productDetails.selectUnit') }}:</span> {{ item.unit }}
                            </span>
                          </div>

                          <!-- Price (shown only if the master toggle is enabled) -->
                          <span v-if="showPrices" class="font-archivo text-base md:text-[22px] leading-[24px] md:leading-[40px] text-black/70">
                            {{ formatItemPrice(item.price, item.quantity, item) }}
                          </span>
                        </div>
                      </div>

                      <!-- Remove button -->
                      <button
                        class="flex-shrink-0 w-6 h-6 md:w-8 md:h-8 flex items-center justify-center hover:bg-[#E30505]/10 transition-colors rounded-sm"
                        @click="removeItem(index)"
                      >
                        <svg width="24" height="24" class="md:w-8 md:h-8" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 4L21.3333 8H10.6667L12 4H20Z" stroke="#E30505" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M4 8H28" stroke="#E30505" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M25.3333 8V26.6667C25.3333 27.0203 25.1929 27.3594 24.9428 27.6095C24.6928 27.8595 24.3536 28 24 28H8C7.64638 28 7.30724 27.8595 7.05719 27.6095C6.80714 27.3594 6.66667 27.0203 6.66667 26.6667V8" stroke="#E30505" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M13.3333 13.3333V22.6667" stroke="#E30505" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M18.6667 13.3333V22.6667" stroke="#E30505" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                      </button>
                    </div>
                  </div>

                  <!-- Totals (shown only if the master toggle is enabled) -->
                  <div v-if="showPrices" class="space-y-4">
                    <!-- Taxes - shown only if tax_rate_enabled is enabled -->
                    <div v-if="showTaxes" class="flex justify-between">
                      <span class="font-archivo text-base md:text-[22px] leading-[24px] md:leading-[40px]">{{ $t('checkout.summary.taxes') }}</span>
                      <span class="font-archivo text-base md:text-[22px] leading-[24px] md:leading-[40px]">{{ currencySymbol }}{{ calculateTaxes }}</span>
                    </div>
                    <!-- Shipping - shown only if shipping_cost_enabled is enabled -->
                    <div v-if="showShipping" class="flex justify-between">
                      <span class="font-archivo text-base md:text-[22px] leading-[24px] md:leading-[40px]">
                        {{ $t('checkout.summary.shipping') }}
                        <span v-if="calculateShipping === '0.00'" class="text-green-600 text-xs md:text-sm ml-1 md:ml-2">
                          ({{ $t('checkout.summary.freeShipping') }})
                        </span>
                      </span>
                      <span class="font-archivo text-base md:text-[22px] leading-[24px] md:leading-[40px]">{{ currencySymbol }}{{ calculateShipping }}</span>
                    </div>
                    <div class="flex justify-between items-center py-4">
                      <span class="font-archivo-narrow font-semibold text-xl md:text-[34px] leading-[30px] md:leading-[40px] text-black/70">
                        {{ $t('checkout.summary.total') }}
                      </span>
                      <span class="font-archivo-narrow font-semibold text-xl md:text-[34px] leading-[30px] md:leading-[40px] text-black/70">
                        {{ currencySymbol }}{{ calculateTotal }}
                      </span>
                    </div>
                  </div>

                  <!-- Minimum purchase value message -->
                  <div v-if="minOrderValueEnabled && !isCheckoutButtonEnabled" class="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
                    {{ minOrderValueMessage }}
                  </div>

                  <!-- Price disclaimer message (editable by the admin) -->
                  <div v-if="checkoutPriceDisclaimer && checkoutPriceDisclaimer.is_active" class="mb-4 p-3 bg-gray-50 border border-gray-200 rounded text-sm md:text-base text-black/70">
                    {{ getCheckoutPriceDisclaimerText() }}
                  </div>

                  <!-- Complete Purchase Button -->
                  <button
                    class="w-full py-3 md:py-4 font-archivo-narrow font-semibold text-xl md:text-[34px] leading-[40px] md:leading-[72px] text-center"
                    :class="{
                      'bg-empire-yellow': isCheckoutButtonEnabled,
                      'bg-gray-300 cursor-not-allowed': !isCheckoutButtonEnabled
                    }"
                    @click="completePurchase"
                    :disabled="!isCheckoutButtonEnabled"
                  >
                    {{ $t('checkout.completePurchase') }}
                  </button>
                </template>

                <template v-else>
                  <div class="flex flex-col items-center gap-6 py-8">
                    <p class="font-archivo-narrow font-semibold text-xl md:text-2xl text-center">{{ $t('checkout.emptyCart') }}</p>
                    <button
                      class="bg-empire-yellow px-6 md:px-8 py-3 md:py-4 font-archivo-narrow font-semibold text-lg md:text-2xl"
                      @click="continueShopping"
                    >
                      {{ $t('checkout.continueShopping') }}
                    </button>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  <AddressSelectionModal
    :show="showAddressModal"
    :current-address-id="currentAddressId"
    :is-pickup-mode="deliveryMethod === 'pickup'"
    :current-location-id="selectedLocationId"
    @close="showAddressModal = false"
    @select="updateShippingAddress"
    @selectLocation="updateSelectedLocation"
  />

  <!-- Project Confirmation Modal -->
  <ProjectConfirmationModal
    :show="showProjectModal"
    @close="closeProjectModal"
    @confirm="confirmProjectAndProceed"
    @change-project="handleProjectChange"
  />

  </div>
</template>

<script>
import { useCartStore } from '@/stores/cartStore'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { computed, ref, onMounted, onUnmounted, watch, getCurrentInstance } from 'vue'
import api from '@/services/api'
import { useI18n } from 'vue-i18n'
import AddressSelectionModal from '@/components/address/AddressSelectionModal.vue'
import ProjectConfirmationModal from '@/components/checkout/ProjectConfirmationModal.vue'
import { useAddressStore } from '@/stores/addressStore'
import { settingsService } from '@/services/settingsService'
import { useCheckoutStore } from '@/stores/checkoutStore'
import { useFinancialTogglesStore } from '@/stores/financialTogglesStore'
import { imageService } from '@/services/imageService' // To use handleImageError
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import { projectService } from '@/services/projectService'
import { locationService } from '@/services/locationService'
import { productService } from '@/services/productService'
import { geographyService } from '@/services/geographyService'
import CustomSelect from '@/components/CustomSelect.vue'
import PickCalendar from '@/components/common/PickCalendar.vue'
import eventBus from '@/utils/eventBus'

export default {
  name: 'CheckoutPage',
  components: {
    AddressSelectionModal,
    ProjectConfirmationModal,
    LoadingSpinner,
    CustomSelect,
    PickCalendar
  },
  setup() {
    const cartStore = useCartStore()
    const store = useStore()
    const router = useRouter()
    const { t, locale } = useI18n()
    const addressStore = useAddressStore()
    const checkoutStore = useCheckoutStore()
    const currencySymbol = ref('$')
    const taxRate = ref(0)
    const shippingCost = ref(0)
    const freeShippingThreshold = ref(0)
    const minOrderValue = ref(0) // Minimum purchase value
    const minOrderValueEnabled = ref(false) // Status of the minimum value toggle
    const togglesStore = useFinancialTogglesStore()
    const showPrices = ref(true) // Controls the price visibility
    const loading = ref(true) // Estado de carregamento principal
    const summaryLoading = ref(true) // Estado de carregamento do resumo
    const error = ref(null) // Error state
    const productDescriptions = ref({}) // Stores the product descriptions
    const hasProjects = ref(false) // Indicates whether the user has associated projects
    const allowPickup = ref(true) // Indicates whether pickup is available (based on business rules)

    const loadFinancialSettings = async () => {
      try {
        const settings = await settingsService.getFinancialSettings()
        currencySymbol.value = settings.currency_symbol
        taxRate.value = parseFloat(settings.tax_rate || 0)
        shippingCost.value = parseFloat(settings.shipping_cost || 0)
        freeShippingThreshold.value = parseFloat(settings.free_shipping_threshold || 0)
        minOrderValue.value = parseFloat(settings.min_order_value || 0) // Minimum purchase value as a number
        minOrderValueEnabled.value = settings.min_order_value_enabled || false // Status do toggle

        // Loads the toggle states
        togglesStore.loadTogglesFromBackend({
          currency_code_enabled: settings.currency_code_enabled,
          currency_symbol_enabled: settings.currency_symbol_enabled,
          tax_rate_enabled: settings.tax_rate_enabled,
          discount_percentage_enabled: settings.discount_percentage_enabled,
          min_order_value_enabled: settings.min_order_value_enabled,
          free_shipping_threshold_enabled: settings.free_shipping_threshold_enabled,
          shipping_cost_enabled: settings.shipping_cost_enabled,
          master_toggle_enabled: settings.master_toggle_enabled
        })

        // Updates the price visibility based on the master toggle
        showPrices.value = togglesStore.masterToggle
        // Updates the status of the minimum value toggle
        minOrderValueEnabled.value = settings.min_order_value_enabled
        console.log('Master toggle state:', togglesStore.masterToggle)
        console.log('Show prices:', showPrices.value)
        console.log('Min order value (raw):', settings.min_order_value)
        console.log('Min order value (ref):', minOrderValue.value)
        console.log('Min order value type:', typeof minOrderValue.value)
        console.log('Min order value enabled:', minOrderValueEnabled.value)
      } catch (error) {
        console.error('Error loading financial settings:', error)
      }
    }

    // onMounted removed here, since it now lives in loadCheckoutData

    const calculateTaxes = computed(() => {
      // Ignores items with price -1 in the calculation
      const subtotalValue = cartItems.value.reduce((total, item) => {
        const itemPrice = Number(item.price)
        if (isNaN(itemPrice) || itemPrice < 0) {
          return total // Ignores items with price -1
        }
        return total + (itemPrice * item.quantity)
      }, 0)
      return (subtotalValue * (taxRate.value / 100)).toFixed(2)
    })

    const calculateShipping = computed(() => {
      // Ignores items with price -1 in the calculation
      const subtotalValue = cartItems.value.reduce((total, item) => {
        const itemPrice = Number(item.price)
        if (isNaN(itemPrice) || itemPrice < 0) {
          return total // Ignores items with price -1
        }
        return total + (itemPrice * item.quantity)
      }, 0)

      // If the total value is above the free shipping threshold, return 0
      if (subtotalValue >= freeShippingThreshold.value) {
        return '0.00'
      }

      // Converts to a number and formats with 2 decimal places
      return Number(shippingCost.value).toFixed(2)
    })

    // Add a computed for the user data
    const userData = computed(() => {
      const user = localStorage.getItem('user')
      return user ? JSON.parse(user) : null
    })

    const cartItems = computed(() => cartStore.items)

    const continueShopping = () => {
      router.push('/categories')
    }

    const removeItem = (index) => {
      cartStore.removeItem(index)
    }

    // Ref variable to control the summary loading state in the setup
    const isLoadingSummaryRef = ref(true)

    // Function to load the descriptions of the products in the cart
    const loadProductDescriptions = async () => {
      try {
        if (cartStore.items.length > 0) {
          console.log('Loading descriptions for', cartStore.items.length, 'items');

          // Creates a copy of the items for modification
          const updatedItems = [...cartStore.items];
          let hasUpdates = false;

          for (let i = 0; i < updatedItems.length; i++) {
            const item = updatedItems[i];
            console.log('Processing item:', item.id, item.name);

            // Checks whether we already have the description cached
            if (productDescriptions.value[item.id]) {
              console.log('Description already in cache for item', item.id);
              // Adds the description directly to the item
              if (!item.description) {
                updatedItems[i] = {
                  ...item,
                  description: productDescriptions.value[item.id]
                };
                hasUpdates = true;
              }
              continue;
            }

            try {
              console.log('Fetching product details for item', item.id);
              const productDetails = await productService.getProductDetails(item.id);

              if (productDetails) {
                // Selects the description in the current language or falls back to English
                const description = productDetails[`description_${locale.value}`] ||
                                   productDetails.description_en ||
                                   productDetails.description_fr ||
                                   '';

                console.log('Got description for item', item.id, ':', description);

                // Armazena no cache
                productDescriptions.value[item.id] = description;

                // Adds the description directly to the item
                updatedItems[i] = {
                  ...item,
                  description: description
                };
                hasUpdates = true;
              }
            } catch (err) {
              console.error('Error fetching product details for item', item.id, ':', err);
            }
          }

          // If there were updates, refresh the items in the cart
          if (hasUpdates) {
            console.log('Updating cart items with descriptions');

            // Updates the items in the store
            for (let i = 0; i < updatedItems.length; i++) {
              if (updatedItems[i].description && !cartStore.items[i].description) {
                console.log(`Adding description to item ${i} (${updatedItems[i].id}): ${updatedItems[i].description.substring(0, 30)}...`);
                // Updates the description in the original item
                cartStore.items[i].description = updatedItems[i].description;
              }
            }
          }
        }
      } catch (error) {
        console.error('Error loading product descriptions:', error);
      }
    }

    // Watches changes in the cart items to load descriptions
    watch(() => cartStore.items.length, () => {
      if (cartStore.items.length > 0) {
        loadProductDescriptions()
      }
    })

    // Watches language changes to refresh the descriptions
    watch(locale, () => {
      if (Object.keys(productDescriptions.value).length > 0) {
        loadProductDescriptions()
      }
    })

    const loadCheckoutData = async () => {
      try {
        console.log('Iniciando carregamento de dados do checkout')
        // Set the loading states to true
        loading.value = true
        summaryLoading.value = true
        isLoadingSummaryRef.value = true
        error.value = null

        // Adding a small delay to make sure the loading state is shown
        await new Promise(resolve => setTimeout(resolve, 300))

        // Loads the financial settings
        await loadFinancialSettings()

        // Ensures a selected project BEFORE fetching addresses (address is per project)
        try {
          const userProjects = await projectService.getCurrentUserProjects()
          const validProjects = (userProjects || []).filter(project => {
            const name = project.name || project.nome || ''
            return name.trim().length > 0
          })
          hasProjects.value = validProjects.length > 0
          console.log('Projetos do usuário (após filtro):', validProjects.length, 'hasProjects:', hasProjects.value)
          const current = projectService.getSelectedProject()
          if (validProjects.length > 0 && (!current || !current.id)) {
            projectService.saveSelectedProject(validProjects[0])
            console.log('Projeto selecionado definido para o primeiro da lista (para endereço de entrega)')
          }
        } catch (err) {
          console.error('Erro ao verificar projetos do usuário:', err)
          hasProjects.value = false
        }

        // Loads the user addresses (linked to the selected project)
        const selectedProject = projectService.getSelectedProject()
        await addressStore.fetchAddresses(selectedProject?.id)

        // Loads the product descriptions
        await loadProductDescriptions()

        // Delivery options (sends the selected project: denonce + main account -> PickUp enabled)
        try {
          const pid = projectService.getSelectedProject()?.id
          const deliveryOptions = await projectService.getDeliveryOptions(pid)
          allowPickup.value = deliveryOptions.allowPickup
          console.log('Opções de entrega:', deliveryOptions)
          if (!deliveryOptions.allowPickup) {
            console.log('PickUp não permitido:', deliveryOptions.reason)
          }
        } catch (err) {
          console.error('Erro ao verificar opções de entrega:', err)
          // On error, allow pickup as a safe default
          allowPickup.value = true
        }

        console.log('Dados básicos carregados, desativando loading principal')
        // Turns off the main loading after the basic data is loaded
        loading.value = false

        console.log('Desativando loading do resumo')
        // Desativa o loading do resumo imediatamente
        summaryLoading.value = false
        isLoadingSummaryRef.value = false
      } catch (err) {
        console.error('Error loading checkout data:', err)
        error.value = 'Failed to load checkout data'
        loading.value = false
        summaryLoading.value = false
        isLoadingSummaryRef.value = false
      }
    }

    const onProjectChanged = async (project) => {
      const vm = getCurrentInstance()?.proxy
      const projectId = project?.id ?? projectService.getSelectedProject()?.id
      await addressStore.fetchAddresses(projectId)
      try {
        const deliveryOptions = await projectService.getDeliveryOptions(projectId)
        allowPickup.value = deliveryOptions.allowPickup
        if (!deliveryOptions.allowPickup && vm?.deliveryMethod === 'pickup') {
          await vm.setDeliveryMethod('delivery')
        }
      } catch (e) {
        console.error('Erro ao atualizar opções de entrega ao trocar projeto:', e)
      }
      window.dispatchEvent(new CustomEvent('loadDefaultAddressNow'))
    }

    onMounted(async () => {
      console.log('🚀 Iniciando carregamento dos dados do checkout...');
      await loadCheckoutData()
      console.log('✅ Dados do checkout carregados');

      const vm = getCurrentInstance()?.proxy
      if (!allowPickup.value && vm?.deliveryMethod === 'pickup') {
        await vm.setDeliveryMethod('delivery')
      }

      eventBus.on('selected-project-changed', onProjectChanged);

      setTimeout(() => {
        console.log('⏰ Emitindo evento para carregar endereço padrão...');
        window.dispatchEvent(new CustomEvent('loadDefaultAddressNow'));
      }, 1000);
    })

    onUnmounted(() => {
      eventBus.off('selected-project-changed', onProjectChanged);
    })

    // Computed properties to check whether the individual toggles are enabled
    const showTaxes = computed(() => togglesStore.fieldToggles.tax_rate)
    const showShipping = computed(() => togglesStore.fieldToggles.shipping_cost)

    return {
      cartStore,
      store,
      router, // Important: pass the router to the methods
      continueShopping,
      cartItems,
      removeItem,
      t,
      userData,
      addressStore,
      currencySymbol,
      calculateTaxes,
      calculateShipping,
      checkoutStore,
      showPrices,
      showTaxes,
      showShipping,
      minOrderValue,
      minOrderValueEnabled,
      loading,
      summaryLoading,
      error,
      loadCheckoutData,
      // Expose the loadFinancialSettings function for use in the methods
      loadFinancialSettings,
      // Expose the isLoadingSummaryRef variable to sync with isLoadingSummary
      isLoadingSummaryRef,
      // Expose the product descriptions
      productDescriptions,
      // Expose hasProjects to check whether the user has projects
      hasProjects,
      // Expose allowPickup to control the pickup button display
      allowPickup
    }
  },
  data() {
    // Initialize formData with the user data when available
    const user = JSON.parse(localStorage.getItem('user') || '{}')

    return {
      sections: {
        personal: true,
        shipping: true,
        payment: true
      },
      isDesktop: window.innerWidth >= 1024,
      deliveryMethod: 'delivery', // Defaults to delivery
      checkoutPriceDisclaimer: null, // Price disclaimer message editable by the admin
      companyData: {
        name: '',
        address: '',
        phone: '',
        email: ''
      },
      companyContacts: [],
      selectedCompanyAddressId: '',
      selectedCompanyAddress: null,
      locations: [], // Lista de locais disponíveis
      selectedLocationId: '', // ID of the selected location
      formData: {
        poNumber: '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '', // User phone number
        address: '', // Address Line 1
        number: '', // Kept for backend compatibility
        landmark: '', // New field: Landmark (replaces neighborhood)
        apartment: '', // Kept for backend compatibility
        city: '',
        state: '',
        postalCode: '',
        country: '',
        contactOnSite: '', // New field: On-site contact
        contactPhone: '', // New field: On-site contact phone
        deliveryDate: '', // New field: Delivery/pickup date
        specialInstructions: '', // New field: Special delivery instructions
        cardHolder: '',
        cardNumber: '',
        expiryDate: '',
        cvv: ''
      },
      showErrors: false,
      /* eslint-disable vue/no-dupe-keys */
      error: null,
      showErrorAlert: false,
      /* eslint-enable vue/no-dupe-keys */
      showAddressModal: false,
      currentAddressId: null,
      showProjectModal: false,
      selectedLocation: null, // Selected location object
      selectedShippingAddressId: null, // ID of the selected shipping address
      addressUserEdited: false, // true when the user typed/selected an address; prevents autofill from overwriting it
      isLoadingSummary: true, // Loading state for the order summary
      // Geography data
      countries: [], // Lista de países
      provinces: [], // Lista de estados/províncias
      selectedCountryId: null, // ID of the selected country
      selectedProvinceId: null // ID of the selected state/province
    }
  },
  computed: {
    // Checks whether there is a selected project in sessionStorage
    hasSelectedProject() {
      try {
        const selectedProject = projectService.getSelectedProject()
        return selectedProject && selectedProject.id !== null && selectedProject.id !== undefined
      } catch (error) {
        console.error('Erro ao verificar projeto selecionado:', error)
        return false
      }
    },
    // Minimum date for selection (tomorrow) - YYYY-MM-DD format for the date input
    minDateISO() {
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      return tomorrow.toISOString().split('T')[0] // Formato YYYY-MM-DD
    },
    // Minimum date for selection (tomorrow) - kept for compatibility
    minDate() {
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      return tomorrow.toISOString().split('T')[0] // Formato YYYY-MM-DD
    },
    subtotal() {
      // Ignores items with price -1 in the calculation
      return this.cartItems.reduce((total, item) => {
        const itemPrice = Number(item.price)
        if (isNaN(itemPrice) || itemPrice < 0) {
          return total // Ignores items with price -1
        }
        return total + (itemPrice * item.quantity)
      }, 0)
    },
    formErrors() {
      const errors = {}

      if (this.deliveryMethod === 'delivery') {
        // Required fields for delivery
        const deliveryRequiredFields = [
          'address',        // Address line 1
          'country',        // Country
          'state',          // State
          'city',           // City
          'postalCode'      // Postal Code
        ]

        deliveryRequiredFields.forEach(field => {
          errors[field] = !this.formData[field]
        })

        console.log('🚚 Validação delivery - campos obrigatórios:', {
          address: !!this.formData.address,
          country: !!this.formData.country,
          state: !!this.formData.state,
          city: !!this.formData.city,
          postalCode: !!this.formData.postalCode
        })
      } else if (this.deliveryMethod === 'pickup') {
        // For pickup: only the selected location is required
        errors.location = !this.selectedLocationId
        console.log('🏪 Validação pickup - local obrigatório:', {
          selectedLocationId: this.selectedLocationId,
          hasLocationError: errors.location
        })
      }

      return errors
    },
    hasErrors() {
      return Object.values(this.formErrors).some(error => error)
    },
    // Add computed properties to check whether fields should be disabled
    isFieldDisabled() {
      return {
        firstName: !!this.userData?.firstName,
        lastName: !!this.userData?.lastName,
        email: !!this.userData?.email
        // We do not include the phone here so it stays editable
      }
    },
    calculateTotal() {
      // Ignores items with price -1 in the calculation
      const subtotalValue = this.cartItems.reduce((total, item) => {
        const itemPrice = Number(item.price)
        if (isNaN(itemPrice) || itemPrice < 0) {
          return total // Ignores items with price -1
        }
        return total + (itemPrice * item.quantity)
      }, 0)

      // Only adds taxes if the toggle is enabled
      const taxesValue = this.showTaxes ? parseFloat(this.calculateTaxes) : 0

      // Only adds shipping if the toggle is enabled
      const shippingValue = this.showShipping ? parseFloat(this.calculateShipping) : 0

      return (subtotalValue + taxesValue + shippingValue).toFixed(2)
    },
    // Checks whether the place order button should be enabled
    isCheckoutButtonEnabled() {
      // If the minimum value toggle is disabled, the button must be enabled
      if (!this.minOrderValueEnabled) {
        console.log('Min order value toggle disabled, button enabled');
        return true;
      }

      // If the toggle is enabled, check whether the total is above the minimum value
      const totalValue = parseFloat(this.calculateTotal);
      const minValue = parseFloat(this.minOrderValue);

      console.log('Checkout validation:', {
        totalValue,
        minValue,
        isEnabled: totalValue >= minValue
      });

      return totalValue >= minValue;
    },
    // Minimum value message to show to the user
    minOrderValueMessage() {
      if (this.minOrderValueEnabled && this.minOrderValue > 0) {
        // Convert to a number before calling toFixed
        const minValue = parseFloat(this.minOrderValue);
        return this.$t('checkout.minOrderValueMessage', { value: `${this.currencySymbol}${minValue.toFixed(2)}` });
      }
      return '';
    },
    // Formatted options for the country combobox
    countryOptions() {
      return this.countries.map(country => ({
        value: country.id,
        label: country.cdesc
      }));
    },
    // Formatted options for the state combobox
    provinceOptions() {
      return this.provinces.map(province => ({
        value: province.id,
        label: province.cdesc
      }));
    }
  },
  async mounted() {
    window.addEventListener('resize', this.checkDesktop)

    // Create a reference for the event handler
    this.handleLoadDefaultAddress = () => {
      console.log('📨 Evento loadDefaultAddressNow recebido');

      if (this.deliveryMethod === 'delivery') {
        console.log('🚚 Modo delivery ativo, carregando endereço padrão...');
        this.loadDefaultAddress();
      } else if (this.deliveryMethod === 'pickup' && this.hasProjects) {
        console.log('📦 Modo pickup ativo e usuário tem projetos, carregando localização do storage...');
        // Loads the location from storage when pickup is active
        this.loadLocations();
      } else {
        console.log('❌ Não está no modo delivery nem pickup com projetos');
      }
    };

    // Listener to load the default address
    window.addEventListener('loadDefaultAddressNow', this.handleLoadDefaultAddress);

    // Load the countries when the component is mounted
    this.loadCountries();

    // If the user has projects and is in pickup mode, load the location from storage
    if (this.hasProjects && this.deliveryMethod === 'pickup') {
      console.log('📦 Usuário tem projetos e modo pickup, carregando localização do storage...');
      await this.loadLocations();
    }

    // Loads the price disclaimer message
    await this.loadCheckoutPriceDisclaimer();

    // We no longer need to set the loading states here,
    // since they are already set in loadCheckoutData
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkDesktop)
    window.removeEventListener('loadDefaultAddressNow', this.handleLoadDefaultAddress)
  },
  methods: {
    // Loads the price disclaimer message
    async loadCheckoutPriceDisclaimer() {
      try {
        const message = await settingsService.getSystemMessage('checkout_price_disclaimer');
        this.checkoutPriceDisclaimer = message;
      } catch (error) {
        console.error('Error loading checkout price disclaimer:', error);
      }
    },
      // Returns the price disclaimer text in the current language
    getCheckoutPriceDisclaimerText() {
      if (!this.checkoutPriceDisclaimer) return '';
      const locale = this.$i18n.locale;
      if (locale === 'fr' && this.checkoutPriceDisclaimer.text_fr) {
        return this.checkoutPriceDisclaimer.text_fr;
      }
      if (locale === 'en' && this.checkoutPriceDisclaimer.text_en) {
        return this.checkoutPriceDisclaimer.text_en;
      }
      if (locale === 'pt' && this.checkoutPriceDisclaimer.text_pt) {
        return this.checkoutPriceDisclaimer.text_pt;
      }
      // Fallback to any available language
      return this.checkoutPriceDisclaimer.text_fr || 
             this.checkoutPriceDisclaimer.text_en || 
             this.checkoutPriceDisclaimer.text_pt || 
             '';
    },
    // Methods to control the loading states
    setLoading(value) {
      // Updates the setup loading variable directly when available
      try {
        if (this.$setup && this.$setup.loading) {
          this.$setup.loading.value = value;
        }
      } catch (err) {
        console.warn('Não foi possível acessar loading do setup:', err);
      }
    },
    setSummaryLoading(value) {
      // Updates the setup summaryLoading variable directly when available
      try {
        if (this.$setup && this.$setup.summaryLoading) {
          this.$setup.summaryLoading.value = value;
        }

        // Also updates isLoadingSummaryRef when available
        if (this.$setup && this.$setup.isLoadingSummaryRef) {
          this.$setup.isLoadingSummaryRef.value = value;
        }
      } catch (err) {
        console.warn('Não foi possível acessar variáveis de loading do setup:', err);
      }

      // Updates the isLoadingSummary variable directly to guarantee synchronization
      this.isLoadingSummary = value;
    },
    getProductImage(imagePath, product) {
      return imageService.getProductImageUrl(imagePath, product)
    },
    handleImageError(e) {
      // Uses the imageService utility function to handle image errors
      imageService.handleImageError(e)
    },
    formatItemPrice(price, quantity = 1, item = null) {
      // If the item has lpor = true, return "Price on request" in the user language
      // Accepts 1, true, or any truthy value
      if (item && (item.lpor === true || item.lpor === 1 || Boolean(item.lpor))) {
        return this.$t('products.priceOnRequest')
      }
      
      if (price === null || price === undefined) {
        return this.$t('products.priceOnRequest')
      }
      const numericPrice = typeof price === 'string' ? parseFloat(price) : Number(price)
      // If the price is NaN or negative, show "Price on request"
      // Price 0.00 is valid and must be displayed normally
      if (isNaN(numericPrice) || numericPrice < 0) {
        return this.$t('products.priceOnRequest')
      }
      const totalPrice = numericPrice * quantity
      return `${this.currencySymbol}${totalPrice.toFixed(2)}`
    },
    // Method to get the ID of the selected location
    getSelectedLocationId() {
      try {
        const locationData = sessionStorage.getItem('selectedLocation')
        if (locationData) {
          const location = JSON.parse(locationData)
          return location.id || null
        }
      } catch (error) {
        console.error('Erro ao obter location selecionada:', error)
      }
      return null
    },
    // Method to get the product description
    getProductDescription(item) {
      if (!item) {
        console.warn('Item is undefined or null');
        return '-';
      }

      console.log('Getting description for item:', item.id);

      // First, check whether the item already has the description
      if (item.description) {
        console.log('Using item description:', item.description);
        return item.description;
      }

      // Then, check whether we have the description in the cache
      if (this.productDescriptions && this.productDescriptions[item.id]) {
        console.log('Found description in cache:', this.productDescriptions[item.id]);
        return this.productDescriptions[item.id];
      }

      // If there is no description, return the product name as a fallback
      console.log('Falling back to item name:', item.name);
      return item.name || '-';
    },
    isWhiteOrLight(color) {
      if (!color || color === 'transparent') return false;

      // Cores claras conhecidas
      const lightColors = ['#ffffff', '#fff', 'white', 'branco', '#f5f5f5', '#fafafa', '#f0f0f0', '#eeeeee', '#e0e0e0', 'lightgray', 'lightgrey'];
      if (lightColors.includes(color.toLowerCase())) {
        return true;
      }

      // Tenta extrair os componentes RGB
      let r, g, b;

      if (color.startsWith('#')) {
        const hex = color.replace('#', '');
        if (hex.length === 3) {
          // Formato abreviado #RGB
          r = parseInt(hex[0] + hex[0], 16);
          g = parseInt(hex[1] + hex[1], 16);
          b = parseInt(hex[2] + hex[2], 16);
        } else if (hex.length === 6) {
          // Formato completo #RRGGBB
          r = parseInt(hex.substr(0, 2), 16);
          g = parseInt(hex.substr(2, 2), 16);
          b = parseInt(hex.substr(4, 2), 16);
        }
      } else if (color.startsWith('rgb')) {
        // Formato rgb(r,g,b) ou rgba(r,g,b,a)
        const rgbValues = color.match(/\d+/g);
        if (rgbValues && rgbValues.length >= 3) {
          [r, g, b] = rgbValues.map(Number);
        }
      }

      // If the RGB components could be extracted, calculate the luminance
      if (r !== undefined && g !== undefined && b !== undefined) {
        // YIQ formula to determine the luminance
        const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
        return yiq >= 128; // Cutoff value for light colors
      }

      // If it could not be determined, assume it is not light
      return false;
    },
    openAddressModal() {
      // First, we calculate the desired position
      const windowHeight = window.innerHeight;
      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      const targetPosition = scrollPosition + (windowHeight * 0.2); // 20% of the window height

      // Fazemos o scroll suave
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });

      // We open the modal after a small delay to let the scroll finish
      setTimeout(() => {
        this.showAddressModal = true;
      }, 300);
    },


    // Method to set the delivery mode
    async setDeliveryMethod(method) {
      console.log('🔄 Mudando modo de entrega para:', method);
      
      this.deliveryMethod = method;

      // When switching to pickup, load the available locations
      if (method === 'pickup') {
        console.log('📦 Modo pickup selecionado, carregando locais...');
        // Always reload the locations to make sure the storage location is used
        await this.loadLocations();
        // Clears the address fields when switching to pickup
        this.clearAddressFields();
      } else if (method === 'delivery') {
        console.log('🚚 Modo delivery selecionado, carregando endereço padrão...');
        // When switching to delivery, load the default address when available
        setTimeout(() => {
          this.loadDefaultAddress();
        }, 100); // Small delay to make sure the change has been processed
      }
    },

    // Method to load the available locations
    async loadLocations() {
      try {
        const locations = await locationService.getLocations();
        this.locations = locations;
        console.log('Locais carregados:', locations);
        // Guard: if the API does not return a valid list, avoid index access
        if (!Array.isArray(locations) || locations.length === 0) {
          console.warn('⚠️ Nenhum local retornado pela API.');
          this.selectedLocationId = '';
          this.selectedLocation = null;
          return;
        }

        // 1. FIRST: Check whether there is a location saved in sessionStorage (from the header)
        const savedLocation = locationService.getSelectedLocation();
        if (savedLocation && savedLocation.id) {
          const locationExists = locations.find(loc => loc.id === savedLocation.id);
          if (locationExists) {
            // Uses the location saved from the header
            this.selectedLocationId = locationExists.id;
            this.selectedLocation = locationExists;
            console.log('🏪 Local selecionado do sessionStorage (header):', {
              id: this.selectedLocationId,
              name: this.selectedLocation.name || this.selectedLocation.description,
              description: this.selectedLocation.description
            });
            return; // Return here so we do not continue with the other logic
          } else {
            console.log('⚠️ Localização salva não encontrada na lista, usando fallback');
          }
        }

        // 2. FALLBACK: Look up the specific location by caracLocation
        const targetLocation = locations.find(loc => loc.caracLocation === '_3ZC0VSY5T');

        if (targetLocation) {
          // Se o local for encontrado, selecione-o
          this.selectedLocationId = targetLocation.id;
          this.selectedLocation = targetLocation;
          console.log('🏪 Local selecionado automaticamente (caracLocation):', {
            id: this.selectedLocationId,
            name: this.selectedLocation.name,
            caracLocation: this.selectedLocation.caracLocation
          });
        } else {
          // 3. LAST FALLBACK: If the location is not found, use the default logic of selecting the first one
          if (Array.isArray(locations) && locations.length > 0) {
            this.selectedLocationId = locations[0].id;
            this.selectedLocation = locations[0];
            console.log('🏪 Local selecionado automaticamente (padrão - primeiro da lista):', {
              id: this.selectedLocationId,
              name: this.selectedLocation.name
            });
          } else {
            console.warn('⚠️ Nenhum local disponível para seleção.');
            this.selectedLocationId = '';
            this.selectedLocation = null;
          }
        }
        // If there is no selected location and locations are available, select the first one
       /* if (!this.selectedLocationId && locations.length > 0) {
          this.selectedLocationId = locations[0].id;
          this.selectedLocation = locations[0];
          console.log('🏪 Local selecionado automaticamente:', {
            id: this.selectedLocationId,
            name: this.selectedLocation.name
          });
        } else {
          console.log('🏪 Estado dos locais:', {
            selectedLocationId: this.selectedLocationId,
            locationsCount: locations.length,
            hasSelectedLocation: !!this.selectedLocationId
          });
        }*/
      } catch (error) {
        console.error('Erro ao carregar locais:', error);
        this.$toast.error(this.$t('checkout.errorLoadingLocations'));
      }
    },

    // Method to load countries
    async loadCountries() {
      try {
        console.log('🌍 Carregando países...');
        const countries = await geographyService.getCountries();
        this.countries = countries;
        console.log('Países carregados:', countries);
      } catch (error) {
        console.error('Erro ao carregar países:', error);
        this.$toast.error(this.$t('checkout.errorLoadingCountries'));
      }
    },

    // Method to load the states/provinces of a country
    async loadProvinces(countryId) {
      try {
        console.log('🏛️ Carregando estados para país:', countryId);
        const provinces = await geographyService.getProvincesByCountry(countryId);
        this.provinces = provinces;
        console.log('Estados carregados:', provinces);
      } catch (error) {
        console.error('Erro ao carregar estados:', error);
        this.$toast.error(this.$t('checkout.errorLoadingStates'));
      }
    },

    // Method called when the country changes
    async onCountryChange(countryId) {
      console.log('🔄 País alterado para:', countryId);

      // Manually editing the country also unlinks the pre-selected address
      this.handleAddressFieldEdit();

      // Updates the selected ID
      this.selectedCountryId = countryId;

      // Clears the selected state
      this.selectedProvinceId = null;
      this.formData.state = '';
      this.provinces = [];

      if (countryId) {
        // Looks up the selected country and updates formData
        const selectedCountry = this.countries.find(c => c.id === countryId);
        if (selectedCountry) {
          this.formData.country = selectedCountry.cdesc;
          console.log('País selecionado:', selectedCountry.cdesc);
        }

        // Loads the states of the selected country
        await this.loadProvinces(countryId);
      } else {
        this.formData.country = '';
      }
    },

    // When the user manually edits any address field, we drop the link to the
    // pre-selected address so the order sends ONLY what was typed
    // (shipping_address_id = null). Avoids an order with new text + old ID.
    handleAddressFieldEdit() {
      // Marks that the user touched the address - autofill can no longer overwrite it
      this.addressUserEdited = true;
      if (this.selectedShippingAddressId || this.currentAddressId) {
        console.log('✏️ Endereço editado manualmente — descartando endereço pré-selecionado');
        this.selectedShippingAddressId = null;
        this.currentAddressId = null;
      }
    },

    // Method called when the state changes
    onProvinceChange(provinceId) {
      console.log('🔄 Estado alterado para:', provinceId);

      // Manually editing the state also unlinks the pre-selected address
      this.handleAddressFieldEdit();

      // Updates the selected ID
      this.selectedProvinceId = provinceId;

      if (provinceId) {
        // Looks up the selected state and updates formData
        const selectedProvince = this.provinces.find(p => p.id === provinceId);
        if (selectedProvince) {
          this.formData.state = selectedProvince.cdesc;
          console.log('Estado selecionado:', selectedProvince.cdesc);
        }
      } else {
        this.formData.state = '';
      }
    },

    // Method to load the default address
    async loadDefaultAddress() {
      try {
        console.log('🔄 Iniciando carregamento do endereço padrão...');
        console.log('📍 Modo de entrega atual:', this.deliveryMethod);
        console.warn('🟥[ADDR-DEBUG] loadDefaultAddress DISPAROU. address atual no form =', JSON.stringify(this.formData.address), '| selectedShippingAddressId =', this.selectedShippingAddressId);

        // Only loads when in delivery mode
        if (this.deliveryMethod !== 'delivery') {
          console.log('❌ Não está no modo delivery, cancelando carregamento');
          return;
        }

        // GUARD: if the user already typed/selected an address, do NOT overwrite it.
        // Prevents an autofill re-trigger (setTimeout, project switch, submit)
        // from overwriting what the user entered with the default address.
        if (this.addressUserEdited) {
          console.warn('🟥[ADDR-DEBUG] loadDefaultAddress PULADO — usuário já editou/selecionou o endereço');
          return;
        }

        // Makes sure the addresses are loaded first (with the selected project)
        if (!this.addressStore.addresses || this.addressStore.addresses.length === 0) {
          console.log('📦 Endereços não carregados, carregando agora...');
          const project = projectService.getSelectedProject();
          await this.addressStore.fetchAddresses(project?.id);
        }

        // Makes sure the countries are loaded
        if (!this.countries || this.countries.length === 0) {
          console.log('🌍 Países não carregados, carregando agora...');
          await this.loadCountries();
        }

        // Looks up the default address (is_default = true)
        const addresses = this.addressStore.addresses;
        console.log('📋 Total de endereços disponíveis:', addresses.length);
        console.log('📋 Endereços completos:', JSON.stringify(addresses, null, 2));

        // Tries to find it by is_default = true first
        let defaultAddress = addresses.find(addr => addr.is_default === true);

        // If not found, try isDefault = true (compatibility)
        if (!defaultAddress) {
          defaultAddress = addresses.find(addr => addr.isDefault === true);
        }

        console.log('🎯 Endereço padrão encontrado:', defaultAddress);

        if (defaultAddress) {
          console.log('✅ Preenchendo formulário com endereço padrão...');

          const addrLine1 = defaultAddress.address_line_1 || '';
          const addrLine2 = defaultAddress.address_line_2 || '';
          this.formData.address = (defaultAddress.formatted_address != null && defaultAddress.formatted_address !== '')
            ? defaultAddress.formatted_address
            : (addrLine2 ? `${addrLine1} ${addrLine2}`.trim() : addrLine1);
          this.formData.landmark = addrLine2;
          this.formData.city = defaultAddress.city || '';
          this.formData.postalCode = defaultAddress.postal_code || '';
          console.warn('🟥[ADDR-DEBUG] loadDefaultAddress SOBRESCREVEU o endereço para =', JSON.stringify(this.formData.address));

          // Selects the country by its authoritative ID (country_id=cpaysid); falls back to the name only when there is no ID
          const countryById = defaultAddress.country_id
            ? this.countries.find(c => c.id === defaultAddress.country_id)
            : null;
          const country = countryById || (defaultAddress.country
            ? this.countries.find(c => c.cdesc === defaultAddress.country)
            : null);
          if (country) {
            console.log('✅ País encontrado:', country);
            this.selectedCountryId = country.id;
            this.formData.country = country.cdesc;

            // Loads the country states and then selects the state by ID (province_id=cprovid)
            await this.loadProvinces(country.id);

            const provinceById = defaultAddress.province_id
              ? this.provinces.find(p => p.id === defaultAddress.province_id)
              : null;
            const province = provinceById || (defaultAddress.state
              ? this.provinces.find(p => p.cdesc === defaultAddress.state)
              : null);
            if (province) {
              console.log('✅ Estado encontrado:', province);
              this.selectedProvinceId = province.id;
              this.formData.state = province.cdesc;
            } else if (defaultAddress.state) {
              console.log('❌ Estado não encontrado:', defaultAddress.state);
              this.selectedProvinceId = null;
              this.formData.state = defaultAddress.state; // Keeps the original name
            }
          } else if (defaultAddress.country) {
            console.log('❌ País não encontrado:', defaultAddress.country);
            this.selectedCountryId = null;
            this.formData.country = defaultAddress.country; // Keeps the original name
          }

          // Saves the ID of the selected address
          this.selectedShippingAddressId = defaultAddress.id;
          this.currentAddressId = defaultAddress.id;

          console.log('📝 Dados preenchidos no formulário:');
          console.log('   - Address:', this.formData.address);
          console.log('   - Landmark:', this.formData.landmark);
          console.log('   - City:', this.formData.city);
          console.log('   - State:', this.formData.state);
          console.log('   - Postal Code:', this.formData.postalCode);
          console.log('   - Country:', this.formData.country);
          console.log('   - Selected Country ID:', this.selectedCountryId);
          console.log('   - Selected Province ID:', this.selectedProvinceId);
          console.log('✅ Formulário preenchido com sucesso!');

          // Forces Vue reactivity
          this.$forceUpdate();
        } else {
          console.log('❌ Nenhum endereço padrão encontrado');
          console.log('🧹 Limpando campos de endereço...');
          this.clearAddressFields();
        }
      } catch (error) {
        console.error('❌ Erro ao carregar endereço padrão:', error);
      }
    },

    // Method to clear the address fields
    clearAddressFields() {
      this.formData.address = '';
      this.formData.landmark = '';
      this.formData.city = '';
      this.formData.state = '';
      this.formData.postalCode = '';
      this.formData.country = '';
      this.selectedShippingAddressId = null;
      this.currentAddressId = null;
      this.selectedCountryId = null;
      this.selectedProvinceId = null;
      this.provinces = []; // Clears the state list
    },



    // Method to update the selected location
    async updateSelectedLocation(location) {
      const previousLocationId = this.selectedLocationId;
      this.selectedLocationId = location.id;
      this.selectedLocation = location;
      console.log('Local selecionado:', location);

      // If the location changed, recalculate the cart prices
      if (previousLocationId && previousLocationId !== location.id) {
        try {
          console.log('🔄 Location mudou, recalculando preços do carrinho...');
          
          // Saves the new location in sessionStorage
          locationService.saveSelectedLocation({
            id: location.id,
            description: location.description
          });

          // Recalculates the cart item prices for the new location
          await this.cartStore.recalculatePrices(location.id);
          console.log('✅ Preços do carrinho recalculados com sucesso para nova location');
          
          // Emits an event to notify the other components
          eventBus.emit('location-changed', {
            id: location.id,
            description: location.description
          });
        } catch (error) {
          console.error('❌ Erro ao recalcular preços do carrinho:', error);
          // On error, keep the location but show a warning
          this.$toast.error(this.$t('checkout.errorRecalculatingPrices'));
        }
      } else if (!previousLocationId) {
        // If there was no previous location, just save it in sessionStorage
        locationService.saveSelectedLocation({
          id: location.id,
          description: location.description
        });
      }

      // Close the modal after the selection
      this.showAddressModal = false;
    },

    // Method to load the company data
    async loadCompanyData() {
      try {
        const response = await api.get('/settings/company');
        this.companyData = {
          name: response.data.company_name || '',
          email: response.data.email || '',
          phone: response.data.phone || '',
          address: response.data.address || ''
        };

        // Maps the contacts to the correct format
        this.companyContacts = (response.data.contacts || []).map(contact => ({
          id: contact.id,
          name: contact.name,
          department: contact.department,
          email: contact.email,
          phone: contact.phone,
          address: contact.address
        }));
      } catch (err) {
        console.error('Error loading company data:', err);
        this.$toast.error(this.$t('contact.loadError'));
      }
    },

    // Method to update the selected company address
    updateSelectedCompanyAddress() {
      if (this.selectedCompanyAddressId === 'main') {
        this.selectedCompanyAddress = {
          name: this.companyData.name,
          address: this.companyData.address
        };
      } else if (this.selectedCompanyAddressId) {
        const contact = this.companyContacts.find(c => c.id === this.selectedCompanyAddressId);
        if (contact) {
          this.selectedCompanyAddress = {
            name: contact.name,
            address: contact.address
          };
        }
      } else {
        this.selectedCompanyAddress = null;
      }
    },

    toggleSection(section) {
      if (!this.isDesktop) {
        this.sections[section] = !this.sections[section]
      }
    },
    checkDesktop() {
      this.isDesktop = window.innerWidth >= 1024
    },
    validateForm() {
      console.log('🔍 Iniciando validação do formulário');
      this.showErrors = true

      console.log('📋 Método de entrega:', this.deliveryMethod);
      console.log('📋 Erros do formulário:', this.formErrors);
      console.log('📋 hasErrors:', this.hasErrors);

      const isValid = !this.hasErrors;
      console.log('✅ Formulário válido:', isValid);

      return isValid;
    },
    async completePurchase() {
      console.log('🚀 completePurchase iniciado');

      try {
        // Checks whether the button is enabled
        console.log('🔍 Verificando se botão está habilitado:', this.isCheckoutButtonEnabled);
        if (!this.isCheckoutButtonEnabled) {
          console.log('❌ Botão não está habilitado, saindo');
          return;
        }

        // Validates the form
        console.log('🔍 Validando formulário...');
        const isFormValid = this.validateForm();
        console.log('📋 Resultado da validação:', isFormValid);

        if (!isFormValid) {
          console.log('❌ Formulário inválido, saindo');
          return;
        }

        // Checks how many projects the user has (same rule for every profile)
        try {
          console.log('🔍 Buscando projetos do usuário...');
          // Fetches the user projects
          const userProjects = await projectService.getCurrentUserProjects();
          // Filters out projects without a name or description
          const validProjects = userProjects.filter(project => {
            const name = project.name || project.nome || ''
            return name.trim().length > 0
          });
          console.log('📋 Projetos encontrados (após filtro):', validProjects);

          if (validProjects.length === 0) {
            // If there are no valid projects, finish without a project
            console.log('⚠️ Usuário não tem projetos válidos, finalizando sem projeto');
            this.confirmProjectAndProceed({ id: null, name: null });
            return;
          } else if (validProjects.length === 1) {
            // If there is only one valid project, finish with that project
            console.log('✅ Usuário tem apenas um projeto válido, finalizando pedido com esse projeto:', validProjects[0]);
            const project = {
              id: validProjects[0].id,
              name: validProjects[0].name || validProjects[0].nome
            };

            // Saves the project in sessionStorage
            projectService.saveSelectedProject(project);

            // Places the order with that project
            this.confirmProjectAndProceed(project);
            return;
          } else {
            // If there are multiple projects, show the modal (for every profile)
            console.log('📋 Usuário tem múltiplos projetos, mostrando modal');
          }
        } catch (error) {
          console.error('❌ Erro ao buscar projetos do usuário:', error);
          // On error, show the modal anyway to allow manual selection
          console.log('🔄 Continuando para mostrar modal devido ao erro');
        }

        // If none of the cases above apply, show the project confirmation modal
        console.log('📋 Mostrando modal de confirmação de projeto');
        this.showProjectModal = true;
        console.log('✅ Modal de projeto definido como true:', this.showProjectModal);

      } catch (error) {
        console.error('❌ Erro inesperado em completePurchase:', error);
        // Shows an error message to the user
        this.$toast.error(this.$t('checkout.errorProcessingOrder'));
      }
    },

    closeProjectModal() {
      this.showProjectModal = false;

      // When closing the modal (Close without projects or overlay), always proceed with creating the order:
      // uses the project in sessionStorage when present, otherwise project_id null (e.g. delivery-only user)
      const storedProject = sessionStorage.getItem('selectedProject');
      const project = storedProject
        ? (() => {
            try {
              return JSON.parse(storedProject);
            } catch {
              return { id: null, name: null };
            }
          })()
        : { id: null, name: null };
      this.confirmProjectAndProceed(project);
    },

    handleProjectChange(project) {
      console.log('Projeto alterado:', project);
      // The project was already saved in localStorage by the modal component
    },

    async confirmProjectAndProceed(project) {
      this.showProjectModal = false;
      console.log('Iniciando processamento do pedido');
      this.setLoading(true); // Ativar o loading global
      this.setSummaryLoading(true); // Ativar o loading do resumo
      this.error = null;

      try {
        // Syncs the cart with the backend
        await this.cartStore.syncCartWithBackend();

        // Updates the user phone when provided
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (user.id && this.formData.phone) {
          try {
            const response = await api.patch(`/users/${user.id}`, {
              firstName: user.firstName,
              lastName: user.lastName,
              phone: this.formData.phone
            });

            // Updates the user data in localStorage
            localStorage.setItem('user', JSON.stringify({
              ...user,
              ...response.data
            }));
          } catch (error) {
            console.error('Error updating user phone:', error);
            // Continues with the checkout even if the phone update fails
          }
        }

        // Use the selected address ID when we are in delivery mode
        let shippingAddressId = null;

        if (this.deliveryMethod === 'delivery') {
          // If the user selected an existing address, we use the ID directly
          if (this.selectedShippingAddressId) {
            shippingAddressId = this.selectedShippingAddressId;
            console.log('Usando endereço existente com ID:', shippingAddressId);
          } else {
            console.log('Nenhum endereço selecionado, usando os dados do formulário sem salvar na tabela shipping_address');
          }
        }

        // We prepare the data for the backend
        // Logic that differs for pickup vs delivery
        const orderData = {
          poNumber: this.formData.poNumber, // Número de PO
          deliveryMethod: this.deliveryMethod,
          // Confirmation email language (FR or EN, according to the store)
          locale: String(this.$i18n.locale || '').toLowerCase() === 'en' ? 'en' : 'fr',
          notes: this.checkoutStore.orderNotes,
          // Sends shipping only if the master toggle AND the individual toggle are enabled
          shippingCost: (this.showPrices && this.showShipping) ? parseFloat(this.calculateShipping) : null,
          // Sends tax only if the master toggle AND the individual toggle are enabled
          taxAmount: (this.showPrices && this.showTaxes) ? parseFloat(this.calculateTaxes) : null,
          // Adds the selected project ID (may be null; avoids an empty string)
          project_id: (project && project.id) ? project.id : null,
          // Adds the selected branch ID (may be null)
          branch_id: this.getSelectedLocationId(),
          // Adds the neovation_order field (always null)
          neovation_order: null,
          specialInstructions: this.formData.specialInstructions || null, // Available for both methods
        };

        // Logic specific to each delivery method
        if (this.deliveryMethod === 'pickup') {
          // For pickup: only location_id, no address data
          orderData.location_id = this.selectedLocationId;
          orderData.address = null;
          orderData.landmark = null;
          orderData.city = null;
          orderData.postalCode = null;
          orderData.shipping_address_id = null;
          orderData.contactOnSite = null;
          orderData.contactPhone = null;
          // Split date and time - always send the time as null
          // Converts from yyyy/mm/dd to yyyy-mm-dd for the backend
          orderData.delivery_date = this.formData.deliveryDate ? this.formData.deliveryDate.replace(/\//g, '-') : null;
          orderData.delivery_time = null;
        } else if (this.deliveryMethod === 'delivery') {
          // For delivery: address data and shipping_address_id when selected
          // Uses the location selected in the header (stored in sessionStorage)
          orderData.location_id = this.getSelectedLocationId();
          orderData.address = this.formData.address;
          orderData.landmark = this.formData.landmark || null;
          orderData.city = this.formData.city;
          orderData.state = this.formData.state || null;
          orderData.postalCode = this.formData.postalCode;
          orderData.country = this.formData.country || null;
          // Incluir IDs de país e estado
          orderData.country_id = this.selectedCountryId || null;
          orderData.state_id = this.selectedProvinceId || null;
          orderData.shipping_address_id = shippingAddressId; // ID of the selected address or null
          orderData.contactOnSite = this.formData.contactOnSite || null;
          orderData.contactPhone = this.formData.contactPhone || null;
          // Split date and time - always send the time as null
          // Converts from yyyy/mm/dd to yyyy-mm-dd for the backend
          orderData.delivery_date = this.formData.deliveryDate ? this.formData.deliveryDate.replace(/\//g, '-') : null;
          orderData.delivery_time = null;
        }

        // We keep the metadata object for compatibility with existing code
        orderData.metadata = {};

        console.log('Order data being sent:', orderData);
        console.warn('🟥[ADDR-DEBUG] PAYLOAD FINAL → address =', JSON.stringify(orderData.address),
          '| city =', JSON.stringify(orderData.city),
          '| postalCode =', JSON.stringify(orderData.postalCode),
          '| state =', JSON.stringify(orderData.state),
          '| country =', JSON.stringify(orderData.country),
          '| country_id =', orderData.country_id,
          '| state_id =', orderData.state_id,
          '| shipping_address_id =', orderData.shipping_address_id);

        const response = await api.post('/orders', orderData);

        if (response.data && response.data.order_number) {
          await this.cartStore.clearCart();
          this.checkoutStore.clearCheckoutData();

          await this.router.push({
            name: 'ThankYou',
            params: { orderNumber: response.data.order_number }
          });
        } else {
          throw new Error('Order number not received from server');
        }
      } catch (error) {
        console.error('Error creating order:', error);
        this.error = this.$t('checkout.errorProcessingOrder');
        this.showErrorAlert = true;
      } finally {
        console.log('Finalizando processamento do pedido');
        this.setLoading(false); // Desativar o loading global
        this.setSummaryLoading(false); // Desativar o loading do resumo
        console.log('Estado final do loading do resumo:', this.isLoadingSummary);
      }
    },
    async updateShippingAddress(address) {
      const line1 = address.address_line_1 || ''
      const line2 = address.address_line_2 || ''
      this.formData.address = (address.formatted_address != null && address.formatted_address !== '')
        ? address.formatted_address
        : (line2 ? `${line1} ${line2}`.trim() : line1)
      this.formData.landmark = line2
      this.formData.city = address.city
      this.formData.postalCode = address.postal_code

      // Country/State: sets the authoritative address IDs (country_id=cpaysid, province_id=cprovid)
      // in the dropdowns. Without this, selectedCountryId/selectedProvinceId went STALE and the order
      // went out with the country_id/state_id of another address (right text, wrong ID).
      if (address.country_id) {
        this.selectedCountryId = address.country_id
        const country = this.countries.find(c => c.id === address.country_id)
        this.formData.country = country ? country.cdesc : (address.country || '')

        await this.loadProvinces(address.country_id)
        if (address.province_id) {
          this.selectedProvinceId = address.province_id
          const province = this.provinces.find(p => p.id === address.province_id)
          this.formData.state = province ? province.cdesc : (address.state || '')
        } else {
          this.selectedProvinceId = null
          this.formData.state = address.state || ''
        }
      } else {
        // Address without geography IDs: fall back to the text and reset the dropdowns
        this.selectedCountryId = null
        this.selectedProvinceId = null
        this.provinces = []
        this.formData.country = address.country || ''
        this.formData.state = address.state || ''
      }

      // Store the ID of the selected address
      this.currentAddressId = address.id

      // Store the address ID to use directly in the order
      this.selectedShippingAddressId = address.id

      // Deliberate user selection: autofill can no longer overwrite it
      this.addressUserEdited = true

      // Close the modal after the selection
      this.showAddressModal = false
    }
  },
  watch: {
    // [ADDR-DEBUG] Tracks every change to the typed address and SHOWS WHO changed it (console.trace)
    'formData.address': {
      handler(newVal, oldVal) {
        console.warn('🟥[ADDR-DEBUG] formData.address MUDOU:', JSON.stringify(oldVal), '→', JSON.stringify(newVal));
        console.trace('🟥[ADDR-DEBUG] stack de quem alterou formData.address');
      }
    },
    'formData.city': {
      handler(newVal, oldVal) {
        console.warn('🟥[ADDR-DEBUG] formData.city MUDOU:', JSON.stringify(oldVal), '→', JSON.stringify(newVal));
      }
    },
    // Add a watcher to update the fields when userData changes
    userData: {
      handler(newUserData) {
        if (newUserData) {
          this.formData.firstName = newUserData.firstName || this.formData.firstName
          this.formData.lastName = newUserData.lastName || this.formData.lastName
          this.formData.email = newUserData.email || this.formData.email
          this.formData.phone = newUserData.phone || this.formData.phone // Also update the phone number
        }
      },
      immediate: true
    },
    // Watcher to sync isLoadingSummaryRef with isLoadingSummary
    '$setup.isLoadingSummaryRef': {
      handler(newValue) {
        console.log('isLoadingSummaryRef mudou para:', newValue)
        this.isLoadingSummary = newValue
      },
      immediate: true
    },
    // Watcher to update the selected address when the ID changes
    selectedCompanyAddressId: {
      handler(newValue) {
        if (newValue) {
          this.updateSelectedCompanyAddress()
        }
      }
    }
  },
  async beforeRouteEnter(_, __, next) {
    const cartStore = useCartStore();

    if (!cartStore.items.length) {
      next('/shopping-cart');
    } else {
      next();
    }
  },
  async created() {
    // We do not load the addresses here, since they are already loaded in loadCheckoutData
    try {
      // Temporarily commented out - do not auto-fill the address fields
      // Will be used in the future when we re-enable this feature
      /*
      const defaultAddress = this.addressStore.addresses.find(addr => addr.isDefault)

      if (defaultAddress) {
        this.formData.address = defaultAddress.address
        this.formData.number = defaultAddress.number // Adicionado number
        this.formData.neighborhood = defaultAddress.neighborhood // Adicionado neighborhood
        this.formData.apartment = defaultAddress.apartment
        this.formData.city = defaultAddress.city
        this.formData.state = defaultAddress.state
        this.formData.postalCode = defaultAddress.postalCode
        this.formData.country = defaultAddress.country
        this.currentAddressId = defaultAddress.id
      }
      */

      // Loads the company data for pickup mode
      this.loadCompanyData();
    } catch (error) {
      console.error('Error loading default address:', error)
      this.$toast.error(this.$t('checkout.errorLoadingAddress'))
    }
  }
}
</script>

<style scoped>
.checkout-page {
  min-height: 100vh;
  background-color: white;
}

input::placeholder {
  color: rgba(0, 0, 0, 0.5);
}

/* Scrollbar styling */
.overflow-y-auto {
  scrollbar-width: 8px;
  scrollbar-color: #FFDD00 #D9D9D9;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #D9D9D9;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #FFDD00;
}

/* Mobile Summary */
.lg\\:hidden {
  display: none;
}

@media (max-width: 1023px) {
  .lg\\:hidden {
    display: block;
  }
}

.section-arrow {
  margin: 0 auto;
  width: 24px;
  height: 24px;
  transform: rotate(90deg);
  transition: transform 0.3s ease;
}

.rotate-270 {
  transform: rotate(270deg);
}

@media (min-width: 1024px) {
  .section-arrow {
    display: none;
  }
}

@media (max-width: 1023px) {
  .mobile-address-btn {
    background-color: #FFCD29; /* empire-yellow */
    padding: 0.5rem 1rem;
    border-radius: 4px;
    font-size: 0.875rem;
    font-weight: 500;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
  }

  .mobile-address-btn:active {
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
}

@media (min-width: 1024px) {
  .mobile-address-btn {
    background-color: #FFCD29;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    font-size: 0.875rem;
    font-weight: 500;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
}

/* Style to keep the description height fixed at exactly two lines */
.description-fixed-height {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  height: 2.4em !important; /* Fixed height for exactly 2 lines */
  min-height: 2.4em !important;
  max-height: 2.4em !important;
  line-height: 1.2em !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  word-break: break-word;
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}

/* Custom style for the product name with extra thin weight */
.product-name {
  font-weight: 100 !important; /* Forces the thinnest possible weight */
  opacity: 0.7 !important; /* Lowers the opacity further so it looks thinner */
  letter-spacing: 0.03em !important; /* Increases the letter spacing further */
  color: rgba(0, 0, 0, 0.6) !important; /* Lighter color so it looks thinner */
  transform: scale(0.98, 1) !important; /* Comprime ligeiramente na horizontal */
}
</style>
